import { useMemo, useState } from "react";
// components
import NoteCodeLayout from "../note-code-layout";
import NoteCode from "../note-code/NoteCode";
import ConfirmModal from "components/confirm-modal";
import TreatmentPlan from "./components/treatment-plan";
// hooks
import { useICDCodesList } from "components/Dashboard/DoctorDashboard/contexts/icd-codes-list-context";
// constants
import messages from "messages";

const DEFAULT = Object.freeze({ id: "", description: "", type: "" });
type Props = {
  codes: ICDCode[];
  onUpdateCodes: (arg: ICDCode[]) => void;
};

type ICDCodeFull = ICDCode & typeof DEFAULT;

const ICDCodes = ({ codes = [], onUpdateCodes }: Props) => {
  const { icdCodesList, busy } = useICDCodesList();

  const icdCodes = useMemo(() => {
    if (!codes.length) {
      // placeholder
      return [DEFAULT] as ICDCodeFull[];
    }

    return codes.map((el) => {
      const matched = icdCodesList[el.id] || "";

      return {
        ...el,
        type: !matched ? messages.others : matched[0].toUpperCase(),
        description: matched,
      };
    });
  }, [codes, icdCodesList]);

  const sortedCodes = useMemo(
    () =>
      Object.entries(icdCodesList)
        .map(([id, description]) => ({
          id,
          description,
          type: (description || "")[0].toUpperCase(),
        }))
        .sort(({ description: a }, { description: b }) =>
          a.localeCompare(b) < 0 ? -1 : 1,
        ),
    [icdCodesList],
  );

  /**
   * options without existing code ids
   */
  const icdCodesOptions = useMemo(() => {
    const codeIds = codes.map((el) => el.id);
    return sortedCodes.filter((el) => !codeIds.includes(el.id));
  }, [codes, sortedCodes]);

  const [deleteModalCodeId, setDeleteModalArrayId] = useState(-1);

  return busy || icdCodes.filter((el) => el.id !== "")?.length === 0 ? null : (
    <>
      <NoteCodeLayout
        title={messages.diagnosis}
        label={messages.icdDiagnosisCode}
      >
        {icdCodes.map((el, i) => (
          <NoteCode
            key={el.id + i || i}
            code={el}
            sortedCodes={icdCodesOptions}
            onCreate={() => onUpdateCodes([...codes, { id: "" }])}
            onUpdate={(nextCode) =>
              onUpdateCodes([
                ...codes.slice(0, i),
                {
                  id: nextCode.id,
                  ...(nextCode.treatment_plan && {
                    treatment_plan: nextCode.treatment_plan,
                  }),
                },
                ...codes.slice(i + 1),
              ])
            }
            onDelete={
              !!el.id || icdCodes.length > 1 || i !== 0
                ? () => {
                    if (el.treatment_plan) {
                      return setDeleteModalArrayId(i);
                    }
                    return onUpdateCodes([
                      ...codes.slice(0, i),
                      ...codes.slice(i + 1),
                    ]);
                  }
                : undefined
            }
            placeholder={messages.searchByICD}
          >
            <TreatmentPlan
              code={el}
              onUpdate={(treatment_plan) =>
                onUpdateCodes([
                  ...codes.slice(0, i),
                  {
                    id: codes[i].id,
                    ...(!treatment_plan ? null : { treatment_plan }),
                  },
                  ...codes.slice(i + 1),
                ])
              }
            />
          </NoteCode>
        ))}
      </NoteCodeLayout>

      <ConfirmModal
        labelTitle={messages.areYouSure}
        children={messages.ifYouDeleteDiagnosis}
        show={deleteModalCodeId > -1}
        onCancel={() => setDeleteModalArrayId(-1)}
        onConfirm={() => {
          onUpdateCodes([
            ...codes.slice(0, deleteModalCodeId),
            ...codes.slice(deleteModalCodeId + 1),
          ]);
          setDeleteModalArrayId(-1);
        }}
      />
    </>
  );
};

export default ICDCodes;
